/**
 * The side-effecting half of the achievement system.
 *
 * `evaluator.ts` is pure and knows nothing about stores. This file is the
 * bridge: it reads every store at once, builds a context, asks the evaluator
 * what is satisfied, and records whatever is newly true. Everything is checked
 * on every call, because a condition can become true through a route nobody
 * predicted (buying a sleeve completes Dressed Up, opening a pack completes
 * Completionist), and a full pass over thirty-odd rows costs nothing.
 *
 * Call sites:
 *   recordMatchForAchievements  the battle screen's game-over path, and the
 *                               matchHistory store's `record` as a backstop
 *   recordPackOpened            the pack opening screen
 *   recordTutorialComplete      the tutorial
 *   checkAchievements           collection, deck builder and store actions
 *
 * Stores are imported by file rather than through the barrel, because the
 * barrel pulls in matchHistory, which calls back into here.
 */

import type { Faction } from '@/engine';
import { allCards } from '@/data';
import { isEquippedNonDefault } from '@/store/cosmetics';
import { useAchievements } from '@/app/stores/achievements';
import { useCollection } from '@/app/stores/collection';
import { useDecks } from '@/app/stores/decks';
import { useProfile } from '@/app/stores/profile';
import type { MatchSummary } from '@/app/stores/matchHistory';
import { evaluateAchievements, monoFactionOf, type AchievementContext } from './evaluator';

/** Build the evaluator context from the current state of every store. */
export function currentContext(match: MatchSummary | null = null): AchievementContext {
  const profile = useProfile.getState();
  const collection = useCollection.getState();
  const achievements = useAchievements.getState();
  const decks = useDecks.getState();

  const ownedCardIds = Object.entries(collection.owned)
    .filter(([, count]) => count > 0)
    .map(([id]) => id);

  return {
    match,
    stats: {
      wins: profile.stats.wins,
      losses: profile.stats.losses,
      gamesPlayed: profile.stats.gamesPlayed,
      rating: profile.stats.rating,
    },
    counters: achievements.counters,
    equipped: {
      sleeve: isEquippedNonDefault(profile.sleeve, 'sleeve'),
      playmat: isEquippedNonDefault(profile.playmat, 'playmat'),
      banner: isEquippedNonDefault(profile.banner, 'banner'),
    },
    savedDeckCount: decks.saved.length,
    ownedCardIds,
    setCardIds: allCards.map((c) => c.id),
  };
}

/**
 * Run a full evaluation and record anything newly satisfied.
 * Returns the ids that were unlocked by this call, which is what a caller
 * wanting to celebrate should react to.
 */
export function checkAchievements(match: MatchSummary | null = null): string[] {
  const satisfied = evaluateAchievements(currentContext(match));
  return useAchievements.getState().unlock(satisfied);
}

/**
 * Called once per finished match. Mono deck wins are remembered here rather
 * than recomputed, because the faction achievements ask "have you ever", and
 * the match history is capped at a hundred entries.
 */
export function recordMatchForAchievements(summary: MatchSummary): string[] {
  if (summary.result === 'win') {
    const mono: Faction | null = monoFactionOf(summary);
    if (mono) useAchievements.getState().recordMonoWin(mono);
  }
  return checkAchievements(summary);
}

/** Called by the pack opening screen once the cards are in the collection. */
export function recordPackOpened(genesisPulled = 0): string[] {
  const store = useAchievements.getState();
  store.countPacksOpened(1);
  if (genesisPulled > 0) store.countGenesisPulled(genesisPulled);
  return checkAchievements();
}

/** Called when the scripted first game is finished. */
export function recordTutorialComplete(): string[] {
  useAchievements.getState().completeTutorial();
  return checkAchievements();
}
